import React from 'react';
import styled from 'styled-components';
import { TrackingEvent } from '@classes/TrackingEvent';
import { CloseButton } from '@ui/buttons/CloseButton';
import { PresetDocumentSelection } from '@containers/DigitalAnalyst/components/PresetDocumentSelection';
import { Modal } from '@ui';
import { Body1, Body5, Container } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { DOC_SELECT_MODES } from '@constants';
import { SpeakerDocumentSelection } from '@containers/DigitalAnalyst/SpeakerDocumentSelection';
import { CompanyTagDocumentSelection } from '@containers/DigitalAnalyst/CompanyTagDocumentSelection';
import { FlatButton } from '@ui/buttons';
import { AllCompanyDocumentSelection } from '@containers/DigitalAnalyst/components/AllCompanyDocumentSelection';
import { DocumentSearchSelection } from '@containers/DigitalAnalyst/components/DocumentSearchSelection';
import { NewsDocumentSelection } from '@containers/DigitalAnalyst/components/NewsDocumentSelection';
import { MyUploadedDocumentSelection } from '@containers/DigitalAnalyst/components/MyUploadedDocumentSelection';
import { Separator } from '@ui';
import { WebsiteUploadsDocumentSelection } from '@containers/DigitalAnalyst/components/WebsiteUploadsDocumentSelection';
import { FedDocumentSelection } from '@containers/DigitalAnalyst/components/FedDocumentSelection';
import { CustomModeDocumentSelection } from '@containers/DigitalAnalyst/components/CustomModeDocumentSelection';
import { SectorDocumentSelection } from '@containers/DigitalAnalyst/components/SectorDocumentSelection';
import { DocumentIdsSelection } from '@containers/DigitalAnalyst/components/DocumentIdsSelection';

const CompanyTagDocumentSelectionWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border} !important;
  }
`;
const DocumentSelectionModesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;
const DocumentSelectionModeWrapper = styled.div`
  padding: 8px 24px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.themeColors.background};
  // border: 1px solid ${({ theme }) => theme.themeColors.text};
  cursor: pointer;
  font-weight: bold;

  &.active {
    background-color: ${({ theme }) => theme.themeColors.text} !important;
    color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;

    * {
      color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;
    }
  }
  &:hover {
    background-color: ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.6)};
    color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;

    * {
      color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;
    }
  }

  * {
    text-transform: uppercase;
  }
`;

const useTrackView = () => {
  React.useEffect(() => {
    TrackingEvent.create('View Document Selection Modal', {});
  }, []);

  return null;
};

const useTrackMode = (docSelectMode) => {
  React.useEffect(() => {
    TrackingEvent.create('Document Selection Mode', {
      Mode: docSelectMode,
    });
  }, [docSelectMode]);

  return null;
};

const generateModeName = (mode) => {
  switch (mode) {
    case DOC_SELECT_MODES.docType:
      return 'Search by document type';
    case DOC_SELECT_MODES.ppm:
      return 'Search by PPM documents/ side letters';
    case DOC_SELECT_MODES.credit:
      return 'Search by credit documents';
    case DOC_SELECT_MODES.speaker:
      return 'Search by speaker';
    case DOC_SELECT_MODES.companyTag:
      return 'Search by Company / Tag';
    case DOC_SELECT_MODES.allCompany:
      return 'Search by Company / Tag';
    case DOC_SELECT_MODES.docName:
      return 'Search by document name';
    case DOC_SELECT_MODES.news:
      return 'Search by news';
    case DOC_SELECT_MODES.myDocs:
      return 'Uploaded documents';
    case DOC_SELECT_MODES.website:
      return 'Website uploads';
    case DOC_SELECT_MODES.fedDoc:
      return 'Search macro documents';
    case DOC_SELECT_MODES.customMode:
      return 'Mode Specific';
    case DOC_SELECT_MODES.sector:
      return 'Search by description';
    default:
      return 'Missing mode';
  }
};

const DocumentSelectionModes = ({ docSelectModes, activeDocSelectMode, onModeChange }) => {
  if (docSelectModes.length === 1) {
    return null;
  }

  return (
    <DocumentSelectionModesWrapper>
      {docSelectModes.map((mode) => (
        <DocumentSelectionModeWrapper
          onClick={() => onModeChange(mode)}
          active={activeDocSelectMode === mode}
          className={activeDocSelectMode === mode ? 'active' : ''}
        >
          <Body5>{generateModeName(mode)}</Body5>
        </DocumentSelectionModeWrapper>
      ))}
    </DocumentSelectionModesWrapper>
  );
};

export const StandaloneDocSelectModal = ({
  heading,
  headerAction = null,
  defaultActiveMode,
  docMode,
  docSelectModes,
  handleAddDocument,
  handleRemoveDocument,
  onDismiss,
  tickersRef,
  selectedDocs,
  tool,
  singleDocMode = false,
}) => {
  const [preSelectedCompanies, setPreSelectedCompanies] = React.useState(tickersRef?.current || []);
  const [modalProps] = React.useState({ maximizeWidth: true, forceHeight: true });
  const [docTypePresetParams] = React.useState({
    docMode,
  });
  const [docSelectMode, setDocSelectMode] = React.useState(defaultActiveMode || docSelectModes[0]);

  React.useEffect(() => {
    if (tickersRef?.current?.length > 0) {
      setPreSelectedCompanies(tickersRef.current);
    }
  }, [tickersRef?.current]);

  useTrackView();
  useTrackMode(docSelectModes);

  const onModeChange = (mode) => setDocSelectMode(mode);

  const isSaveEnabled = docSelectMode !== DOC_SELECT_MODES.sector;

  return (
    <Modal onDismiss={onDismiss} modalProps={modalProps}>
      <Container row verticallyCenter spaceBetween>
        <Container row verticallyCenter spaceBetween fullWidth>
          <Container>
            <Body1 bold>{heading || 'Select Source Documents'}</Body1>
          </Container>
        </Container>
        <Container top={0} left={16}>
          <Container row verticallyCenter>
            {isSaveEnabled && <FlatButton onClick={onDismiss}>Save</FlatButton>}
            <Container left={16}>
              <CloseButton onClick={onDismiss} />
            </Container>
          </Container>
        </Container>
      </Container>
      {headerAction}
      <Container top={28}>
        <DocumentSelectionModes
          activeDocSelectMode={docSelectMode}
          docSelectModes={docSelectModes}
          onModeChange={onModeChange}
        />
      </Container>
      <Container top={24} bottom={16}>
        <Separator />
      </Container>
      <Container top={0}>
        {docSelectMode === DOC_SELECT_MODES.website && (
          <WebsiteUploadsDocumentSelection
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
        {docSelectMode === DOC_SELECT_MODES.allCompany && (
          <AllCompanyDocumentSelection
            preSelectedCompanies={preSelectedCompanies}
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
        {docSelectMode === DOC_SELECT_MODES.docName && (
          <DocumentSearchSelection
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
        {docSelectMode === DOC_SELECT_MODES.news && (
          <NewsDocumentSelection
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
        {docSelectMode === DOC_SELECT_MODES.fedDoc && (
          <FedDocumentSelection
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
            selectedDocs={selectedDocs}
          />
        )}

        {docSelectMode === DOC_SELECT_MODES.speaker && (
          <SpeakerDocumentSelection
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
            selectedDocs={selectedDocs}
          />
        )}
        {docSelectMode === DOC_SELECT_MODES.docType && (
          <Container top={36}>
            <PresetDocumentSelection
              docMode={docMode}
              presetParams={docTypePresetParams}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.ppm && (
          <Container top={36}>
            <PresetDocumentSelection
              docMode={docMode}
              presetParams={{ docMode: 'ppm' }}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.credit && (
          <Container top={36}>
            <PresetDocumentSelection
              docMode={docMode}
              presetParams={{ docMode: 'credit' }}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.sector && (
          <Container top={24}>
            <SectorDocumentSelection handleAddDocuments={handleAddDocument} onDismiss={onDismiss} />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.companyTag && (
          <CompanyTagDocumentSelectionWrapper>
            <CompanyTagDocumentSelection
              selectedDocs={selectedDocs}
              onSelect={handleAddDocument}
              onRemoveSelect={handleRemoveDocument}
            />
          </CompanyTagDocumentSelectionWrapper>
        )}
        {docSelectMode === DOC_SELECT_MODES.client && (
          <Container top={36}>
            <PresetDocumentSelection
              docSelectMode={docSelectMode}
              presetParams={docTypePresetParams}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.myDocs && (
          <Container top={0}>
            <MyUploadedDocumentSelection
              presetParams={docTypePresetParams}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
              singleDocMode={singleDocMode}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.customMode && (
          <Container top={36}>
            <CustomModeDocumentSelection
              presetParams={docTypePresetParams}
              handleAddDocument={handleAddDocument}
              handleRemoveDocument={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onDismiss={onDismiss}
              tool={tool}
            />
          </Container>
        )}
        {docSelectMode === DOC_SELECT_MODES.ids && (
          <DocumentIdsSelection
            selectedDocIds={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
      </Container>
    </Modal>
  );
};
